/**
 * useTopicKeywordLinker – Hook der Topic-Keywords lädt und HTML-Content
 * mit klickbaren Links zu den Topic-Detailseiten anreichert.
 *
 * Nutzung:
 *   const { renderLinkedHTML } = useTopicKeywordLinker();
 *   <div dangerouslySetInnerHTML={{ __html: renderLinkedHTML(post.content) }} />
 *
 * Regeln:
 *  - Längere Keywords gewinnen vor kürzeren (z.B. „Gips gießen" vor „Gips")
 *  - Pro Keyword wird in einem Text-Block nur das erste Vorkommen verlinkt,
 *    um Link-Spam zu vermeiden
 *  - HTML-Tags und bestehende Anker werden nicht angefasst
 */
import { useEffect, useState, useCallback } from 'react';
import axiosInstance from '../api/axios';

const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export default function useTopicKeywordLinker() {
  const [topics, setTopics] = useState([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axiosInstance.get('/blog/topics');
        if (alive) setTopics(Array.isArray(r.data) ? r.data : []);
      } catch {
        if (alive) setTopics([]);
      }
    })();
    return () => { alive = false; };
  }, []);

  const renderLinkedHTML = useCallback((html) => {
    if (!html || !topics.length) return html || '';

    // Build [keyword → slug] list, longest first
    const all = [];
    topics.forEach(t => (t.keywords || []).forEach(k => {
      const kw = (k || '').trim();
      if (kw) all.push({ kw, slug: t.slug });
    }));
    all.sort((a, b) => b.kw.length - a.kw.length);

    // Track already-linked keywords (case-insensitive) per slug
    const linkedSlugs = new Set();

    // Tokenise HTML into tags + text nodes so we only touch text
    const tokens = html.split(/(<[^>]+>)/);

    return tokens.map(tok => {
      if (tok.startsWith('<')) return tok; // tag — leave untouched
      let text = tok;
      for (const { kw, slug } of all) {
        if (linkedSlugs.has(slug)) continue;
        const re = new RegExp(`(^|[^\\p{L}])(${escapeRegex(kw)})([^\\p{L}]|$)`, 'iu');
        const m = re.exec(text);
        if (m) {
          const start = m.index + m[1].length;
          const end = start + m[2].length;
          const before = text.slice(0, start);
          const matched = text.slice(start, end);
          const after = text.slice(end);
          text = `${before}<a href="/blog/themen/${slug}" class="topic-link" data-testid="topic-link-${slug}">${matched}</a>${after}`;
          linkedSlugs.add(slug);
        }
      }
      return text;
    }).join('');
  }, [topics]);

  return { renderLinkedHTML, topics };
}
